import { proxy } from 'valtio';
import { ChatStoreData, ChatMessage } from '../types';

export interface ChatActions {
  addMessage: (message: ChatMessage) => void;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  removeMessage: (id: string) => void;
  clearMessages: () => void;
  setIsAIGenerating: (isGenerating: boolean) => void;
  setPendingEditorUpdates: (updates: unknown[]) => void;
  addPendingEditorUpdate: (update: unknown) => void;
  clearPendingEditorUpdates: () => void;
  setWsProgress: (progress: number | undefined) => void;
  setWsStatus: (status: boolean | undefined) => void;
  // Issue #506: 入力状態管理のアクション
  setCurrentInput: (input: string) => void;
  clearCurrentInput: () => void;
  setSourcePolicyState: (data: {
    rawSelectedSourceIds?: string[];
    effectiveSelectedSourceIds?: string[];
    policyHash?: string | null;
    resolvedPolicy?: unknown;
  }) => void;
  setPolicyMismatchWarning: (warning: string | null) => void;
  // AI生成キャンセル機能
  requestCancel: () => void;
  isJobCancelled: (jobId: string) => boolean;
  setCurrentJobId: (jobId: string | null) => void;
  clearCancelledJobs: () => void;
}

export function createChatStore(initialState?: Partial<ChatStoreData>) {
  const store = proxy<ChatStoreData>({
    messages: [],
    isAIGenerating: false,
    pendingEditorUpdates: [],
    currentInput: '', // Issue #506: 初期値を空文字列に設定
    rawSelectedSourceIds: [],
    effectiveSelectedSourceIds: [],
    policyHash: null,
    resolvedPolicy: undefined,
    policyMismatchWarning: null,
    // AI生成キャンセル機能
    isCancelling: false,
    cancelledJobIds: new Set<string>(),
    currentJobId: null,
    cancelTimeoutId: null,
    ...initialState,
  });

  const actions: ChatActions = {
    addMessage(message: ChatMessage) {
      store.messages.push(message);
    },

    updateMessage(id: string, updates: Partial<ChatMessage>) {
      const index = store.messages.findIndex(m => m.id === id);
      if (index !== -1) {
        store.messages[index] = {
          ...store.messages[index],
          ...updates,
        };
      }
    },

    removeMessage(id: string) {
      const index = store.messages.findIndex(m => m.id === id);
      if (index !== -1) {
        store.messages.splice(index, 1);
      }
    },

    clearMessages() {
      store.messages = [];
    },

    setIsAIGenerating(isGenerating: boolean) {
      store.isAIGenerating = isGenerating;
    },

    setPendingEditorUpdates(updates: unknown[]) {
      store.pendingEditorUpdates = updates;
    },

    addPendingEditorUpdate(update: unknown) {
      store.pendingEditorUpdates.push(update);
    },

    clearPendingEditorUpdates() {
      store.pendingEditorUpdates = [];
    },
    
    setWsProgress(progress: number | undefined) {
      store.wsProgress = progress;
    },
    
    setWsStatus(status: boolean | undefined) {
      store.wsStatus = status;
    },

    // Issue #506: 入力状態管理のアクション実装
    setCurrentInput(input: string) {
      store.currentInput = input;
    },

    clearCurrentInput() {
      store.currentInput = '';
    },

    setSourcePolicyState({ rawSelectedSourceIds, effectiveSelectedSourceIds, policyHash, resolvedPolicy }) {
      if (Array.isArray(rawSelectedSourceIds)) {
        store.rawSelectedSourceIds = Array.from(new Set(rawSelectedSourceIds));
      }
      if (Array.isArray(effectiveSelectedSourceIds)) {
        store.effectiveSelectedSourceIds = Array.from(new Set(effectiveSelectedSourceIds));
      }
      if (policyHash !== undefined) {
        store.policyHash = policyHash;
      }
      if (resolvedPolicy !== undefined) {
        store.resolvedPolicy = resolvedPolicy;
      }
    },

    setPolicyMismatchWarning(warning: string | null) {
      store.policyMismatchWarning = warning ?? null;
    },

    // AI生成キャンセル機能
    requestCancel() {
      // 1. キャンセル中フラグを立てる
      store.isCancelling = true;

      // 2. 現在のジョブIDをcancelledJobIdsに追加
      if (store.currentJobId) {
        store.cancelledJobIds.add(store.currentJobId);
      }

      // 3. 即座に生成中フラグをOFF
      store.isAIGenerating = false;

      // 4. 既存タイマーをクリアしてから新しいタイマーを設定（競合状態を防止）
      if (store.cancelTimeoutId !== null) {
        clearTimeout(store.cancelTimeoutId);
      }
      store.cancelTimeoutId = setTimeout(() => {
        store.isCancelling = false;
        store.cancelTimeoutId = null;
      }, 300);
    },

    isJobCancelled(jobId: string): boolean {
      return store.cancelledJobIds.has(jobId);
    },

    setCurrentJobId(jobId: string | null) {
      store.currentJobId = jobId;
    },

    clearCancelledJobs() {
      store.cancelledJobIds.clear();
      store.currentJobId = null;
      if (store.cancelTimeoutId !== null) {
        clearTimeout(store.cancelTimeoutId);
        store.cancelTimeoutId = null;
      }
    },
  };

  return { store, actions };
}

export type ChatStoreInstance = ReturnType<typeof createChatStore>;
